import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import { Dispatch, RootState } from "core/store/store.types";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Text } from "components/atoms/text";
import { User } from "core/types/user.types";
import { authAsync, authSelectors } from "core/store/modules/auth";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  searchActions,
  searchAsync,
  searchSelectors,
} from "core/store/modules/search";
import { AutoConf } from "core/types/search.types";
import { SubscriptionPlan } from "containers/my-profile/subscription-plan";
import { EmptyState } from "components/atoms/empty-state";
import AddIcon from "@mui/icons-material/Add";
import { customOrange } from "core/theme/variants";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

enum Status {
  NOT_PURCHASED,
  INITIALIZED,
  DONE,
}

interface OwnProps {}
interface StateProps {
  user?: User;
  isAutoLoading: boolean;
  autoElements?: AutoConf;
  success?: string;
  error?: string;
}
interface DispatchProps {
  getUser: () => void;
  getAutoSearchConfigList: () => void;
  addNewParam: (name: string) => void;
  editAutoName: (id: string, name: string) => void;
  removeParam: (id: string) => void;
  addAutoConfigName: (name: string) => void;
  addFirstParams: (params: string[]) => void;
  resetSuccess: () => void;
  resetError: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const ConfigurationPage: FunctionComponent<Props> = ({
  user,
  isAutoLoading,
  autoElements,
  getUser,
  getAutoSearchConfigList,
  addNewParam,
  editAutoName,
  removeParam,
  addAutoConfigName,
  addFirstParams,
  success,
  error,
  resetSuccess,
  resetError,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<{ id: number; name: string }[]>([]);
  const theme = useTheme();
  const [addRow, setAddRow] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [editName, setEditName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState<Status>();
  const [blacklistParams, setBlackListParams] = useState<string[]>([]);
  const [param, setParam] = useState("");

  useEffect(() => {
    getUser();
    getAutoSearchConfigList();
  }, []);

  useEffect(() => {
    if (autoElements && user) {
      setData(autoElements.params);
      setEditedName(autoElements.fullName);
      if (user.subscriptions) {
        if (autoElements.id) {
          setSubscriptionStatus(Status.DONE);
        } else {
          setSubscriptionStatus(Status.INITIALIZED);
        }
      } else {
        setSubscriptionStatus(Status.NOT_PURCHASED);
      }
    }
  }, [autoElements, user]);

  useEffect(() => {
    if (addRow) {
      setData((prev) => [...(prev ?? []), { id: 0, name: "" }]);
    }
  }, [addRow]);

  const handleFieldChange = (value: string) => {
    if (data) {
      const modifiedData = [...data];
      modifiedData[data.length - 1].name = value;
      setData([...modifiedData]);
    }
  };

  const handleNewParam = () => {
    if (data) {
      const newFilmTitle = data[data.length - 1].name;
      addNewParam(newFilmTitle);
      setAddRow(false);
    }
  };

  useEffect(() => {
    if (success !== "") {
      getAutoSearchConfigList();
      setOpenSnackbar(true);
      setEditName(false);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setOpenSnackbar(true);
      setEditName(false);
    }
  }, [error]);

  const handleAddParam = () => {
    if (param.length > 0) {
      setBlackListParams([...blacklistParams, param]);
      setParam("");
    }
  };

  const removeFirstTimeParam = (param: string) => {
    const updatedParams = blacklistParams.filter((p) => p !== param);
    setBlackListParams(updatedParams);
  };

  const handleNewFirstConfig = () => {
    addAutoConfigName(editedName);
    addFirstParams(blacklistParams);
    setEditedName("");
    setBlackListParams([]);
  };

  const getConfigPageContent = () => {
    switch (subscriptionStatus) {
      case Status.NOT_PURCHASED:
        return (
          <Grid container>
            <Text textAlign={"center"} width={"100%"}>
              {t("empty_states.empty_config.no_sub_title")}
            </Text>
            <SubscriptionPlan />
          </Grid>
        );
      case Status.INITIALIZED:
        return (
          <>
            <EmptyState
              title={t("empty_states.empty_config.empty_list_title")}
              description={t("empty_states.empty_config.empty_list_subtitle")}
            />
            <Grid
              container
              paddingY={"15px"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  type="text"
                  size="small"
                  label={t("auto_conf.name")}
                  variant="standard"
                  value={editedName}
                  InputLabelProps={{
                    style: {
                      width: "inherit",
                      fontSize: "14px !important",
                      fontWeight: "700",
                    },
                  }}
                  onChange={(e) => setEditedName(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid
              container
              paddingY={"15px"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              spacing={3}
            >
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    label={t("new-search.fields.blacklist")}
                    value={param}
                    onChange={(e) => setParam(e.target.value)}
                    fullWidth
                    variant="standard"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddParam();
                      }
                    }}
                    InputLabelProps={{
                      style: {
                        width: "inherit",
                        fontSize: "14px !important",
                        fontWeight: "700",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => handleAddParam()}
                            onDoubleClick={() => handleAddParam()}
                            aria-label="Add"
                          >
                            <AddIcon color="info" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item container xs={6} spacing={2}>
                {blacklistParams.map((param, index) => (
                  <Grid key={index} item xs={3}>
                    <Paper
                      sx={{
                        backgroundColor: customOrange[50],
                        borderStyle: "solid",
                        borderColor: `${theme.palette.primary.main}`,
                        borderWidth: "1px",
                        padding: "5px 10px",
                        borderRadius: "5px",
                      }}
                    >
                      <Grid container>
                        <Grid item xs={10}>
                          <Text
                            fontSize={"13px"}
                            fontWeight={"700"}
                            sx={{
                              color: "#1D2C54",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {param}
                          </Text>
                        </Grid>

                        <Grid item xs={2}>
                          <IconButton
                            onClick={() => removeFirstTimeParam(param)}
                            style={{
                              padding: 0,
                              width: "20px",
                              height: "20px",
                            }}
                          >
                            <CloseRoundedIcon
                              sx={{ color: "#1D2C54", width: "16px" }}
                            />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid container paddingY={"15px"}>
              <Grid item xs={12} display={"flex"} justifyContent={"flex-end"}>
                <Button
                  variant="contained"
                  onClick={() => handleNewFirstConfig()}
                >
                  {t("auto_conf.confirm")}
                </Button>
              </Grid>
            </Grid>
          </>
        );
      case Status.DONE:
        return (
          <Paper
            sx={{
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <Grid
              container
              paddingY={"15px"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid item xs={5}>
                {editName ? (
                  <TextField
                    fullWidth
                    type="text"
                    size="small"
                    variant="standard"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                  />
                ) : (
                  <Text fontSize={"16px"} fontWeight={"700"}>
                    {autoElements?.fullName}
                  </Text>
                )}
              </Grid>
              <Grid item xs={7} display={"flex"} justifyContent={"flex-end"}>
                {editName ? (
                  <div>
                    <Grid container spacing={3}>
                      <Grid item xs={6} display={"flex"} alignItems={"center"}>
                        <IconButton
                          onClick={() =>
                            autoElements &&
                            editAutoName(autoElements.id.toString(), editedName)
                          }
                        >
                          <SaveIcon color="primary" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setEditName(false);
                            autoElements &&
                              setEditedName(autoElements.fullName);
                          }}
                        >
                          <CancelRoundedIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                ) : (
                  <div>
                    <Grid container spacing={3}>
                      <Grid item xs={6} display={"flex"} alignItems={"center"}>
                        <IconButton
                          onClick={() => {
                            setEditName(true);
                            autoElements &&
                              setEditedName(autoElements.fullName);
                          }}
                        >
                          <EditIcon color="primary" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={6}>
              <Grid
                item
                xs={12}
                sm={6}
                display={"inline-block"}
                maxWidth={"100%"}
              >
                <Text fontSize={"16px"} fontWeight={"500"}>
                  {t("auto_conf.max_research")}:{" "}
                  {user?.subscriptions?.max_titles}
                </Text>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                display={"inline-block"}
                maxWidth={"100%"}
              >
                <Text fontSize={"16px"} fontWeight={"500"}>
                  {t("auto_conf.inserted")}: {autoElements?.params?.length}
                </Text>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                {data && data.length > 0 && (
                  <TableContainer
                    component={Paper}
                    style={{
                      position: "relative",
                      borderRadius: "8px",
                      maxHeight: "500px",
                      marginTop: "20px",
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("auto_conf.head_title")}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody
                        sx={{
                          "& .MuiTableCell-root": {
                            padding: "10px",
                            fontSize: "14px",
                          },
                        }}
                      >
                        {data?.map((film, idx) =>
                          addRow && idx === data.length - 1 ? (
                            <TableRow>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="text"
                                  size="small"
                                  variant="standard"
                                  value={data[data?.length - 1].name}
                                  onChange={(e) =>
                                    handleFieldChange(e.target.value)
                                  }
                                />
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          ) : (
                            <TableRow key={idx}>
                              <TableCell>{film.name}</TableCell>
                              <TableCell sx={{ minWidth: "200px" }}>
                                <Grid container spacing={3}>
                                  <Grid
                                    item
                                    xs={6}
                                    display={"flex"}
                                    alignItems={"center"}
                                  >
                                    <IconButton
                                      onClick={() => {
                                        if (film.id)
                                          removeParam(film.id.toString());
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>

            <Grid container>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: "40px",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => (addRow ? handleNewParam() : setAddRow(true))}
                >
                  {addRow ? t("auto_conf.confirm") : t("auto_conf.add")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        );
    }
  };

  return (
    <>
      {isAutoLoading ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <CircularProgress color="secondary" size={"100px"} />
        </div>
      ) : (
        getConfigPageContent()
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        onClose={() => {
          setOpenSnackbar(false);
          resetError();
          resetSuccess();
        }}
      >
        <Alert
          onClose={() => {
            setOpenSnackbar(false);
            resetError();
            resetSuccess();
          }}
          severity={error ? "error" : "success"}
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {error ? error : success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    user: authSelectors.getUser(state),
    autoElements: searchSelectors.automaticConfigElements(state),
    isAutoLoading: searchSelectors.isAutoLoading(state),
    error: searchSelectors.getAutoError(state),
    success: searchSelectors.getAutoSuccess(state),
  }),
  (dispatch: Dispatch) => ({
    getUser: () => dispatch(authAsync.getUser()),
    getAutoSearchConfigList: () => dispatch(searchAsync.getAutoSearchConfig()),
    removeParam: (id: string) =>
      dispatch(searchAsync.removeAutoConfigParam(id)),
    addNewParam: (name: string) =>
      dispatch(searchAsync.addAutoConfigParam(name)),
    editAutoName: (id: string, name: string) =>
      dispatch(searchAsync.editAutoConfigName(id, name)),
    addAutoConfigName: (name: string) =>
      dispatch(searchAsync.addAutoConfigName(name)),
    addFirstParams: (params: string[]) =>
      dispatch(searchAsync.addFirstParams(params)),
    resetSuccess: () => dispatch(searchActions.success({ success: "" })),
    resetError: () => dispatch(searchActions.error({ error: "" })),
  })
)(ConfigurationPage);
